import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, Lock } from 'lucide-react';
import { toast } from 'sonner';
import PatientLayout from '@/components/PatientLayout';
import PDFViewer from '@/components/PDFViewer';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const PatientArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const response = await fetch(`${API}/api/patient-articles/slug/${slug}`);
      if (response.ok) {
        const data = await response.json();
        if (data.access_level === 'members_only') {
          const user = JSON.parse(localStorage.getItem('user') || 'null');
          if (!user || !['super_admin', 'regional', 'ophthalmologist', 'intern'].includes(user.role)) {
            toast.error('Cet article est réservé aux membres');
            navigate('/patients');
            return;
          }
        }
        setArticle(data);
        fetchRelatedArticles(data.category_id, data.id);
      } else {
        toast.error('Article non trouvé');
        navigate('/patients');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async (categoryId, currentArticleId) => {
    try {
      const response = await fetch(`${API}/api/patient-articles?category_id=${categoryId}&status=published`);
      if (response.ok) {
        const data = await response.json();
        setRelatedArticles(data.filter(a => a.id !== currentArticleId).slice(0, 3));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1e3a5f]"></div>
        </div>
      </PatientLayout>
    );
  }

  if (!article) return null;

  return (
    <PatientLayout>
      <SEO
        title={article.title}
        description={article.excerpt || `${article.title} - Espace Patients SFOALC`}
        canonical={`https://sfo-alc.fr/patients/article/${article.slug}`}
        type="article"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-3">
          <Link to="/patients" className="inline-flex items-center gap-2 text-[#35AFE4] hover:text-[#1e3a5f] text-sm transition-colors" data-testid="back-to-patients">
            <ArrowLeft size={16} />
            <span>Retour à l'espace patients</span>
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <div
        className="text-white py-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6187 50%, #35AFE4 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="flex items-center gap-2 text-sm text-white/80 mb-3">
            <span>{article.category_name}</span>
            {article.access_level === 'members_only' && (
              <>
                <span>&bull;</span>
                <Lock size={14} />
                <span>Réservé aux membres</span>
              </>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <div className="flex items-center gap-1.5">
              <Calendar size={14} />
              <span>{new Date(article.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Eye size={14} />
              <span>{article.views || 0} vues</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" className="w-full h-12 block">
            <path fill="#f9fafb" d="M1000,4.3V0H0v4.3C0.9,23.1,126.7,99.2,500,100S1000,22.7,1000,4.3z" style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }} />
          </svg>
        </div>
      </div>

      {/* Article Content */}
      <div className="bg-gray-50 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100" data-testid="article-content">
            {article.featured_image && (
              <img src={article.featured_image} alt={article.title} className="w-full h-72 object-cover rounded-lg mb-6" />
            )}

            {article.video_url && (
              <div className="mb-6 aspect-video rounded-lg overflow-hidden bg-black" data-testid="video-player">
                {article.video_url.endsWith('.mp4') ? (
                  <video
                    src={article.video_url.startsWith('http') ? article.video_url : `${API}${article.video_url}`}
                    controls
                    className="w-full h-full"
                    preload="metadata"
                    playsInline
                  >
                    Votre navigateur ne supporte pas la lecture vidéo.
                  </video>
                ) : (
                  <iframe src={article.video_url} className="w-full h-full" allowFullScreen title={article.title} />
                )}
              </div>
            )}

            <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed">
              <div className="rich-content" dangerouslySetInnerHTML={{ __html: article.content?.replace(/\n/g, '<br />') || '' }} />
            </div>

            {article.pdf_url && (
              <div className="mt-8" data-testid="pdf-section">
                <PDFViewer url={article.pdf_url} title={article.title} />
              </div>
            )}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-10">
              <h2 className="text-xl font-bold text-gray-900 mb-5" style={{ fontFamily: "'Barlow', sans-serif" }}>Articles similaires</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="related-articles">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/patients/article/${related.slug}`}
                    className="group bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all border border-gray-100"
                    data-testid={`related-article-${related.slug}`}
                  >
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#35AFE4] transition-colors text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {related.title}
                    </h3>
                    {related.excerpt && (
                      <p className="text-gray-500 text-sm line-clamp-2">{related.excerpt}</p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </PatientLayout>
  );
};

export default PatientArticlePage;
